import type { FieldDefinition } from '@/interfaces/fields-management'

/**
 * Secciones que son tablas —filas propias o subcolecciones— y no campos del
 * documento del embarque. Sus booleanos pertenecen a la fila, no al embarque.
 */
const ROW_SECTIONS = ['items', 'packages', 'customs', 'charges']

/**
 * Un campo boolean se pinta como un toggle que muestra "No" aunque el documento no
 * traiga el valor, así que "No" y "sin responder" se ven igual. Un campo calculado
 * que dependa de él sí los distingue —undefined no cumple ninguna condición— y se
 * queda vacío mientras el usuario ya está viendo un "No".
 *
 * Al crear el embarque se persiste el valor que el toggle muestra desde el principio,
 * de forma que el cálculo arranque con el mismo dato que ve el usuario.
 *
 * Solo rellena lo que falta: un valor que venga en el payload (creación desde una OC,
 * duplicado, importación con esa columna mapeada) manda sobre el default.
 */
export const booleanFieldDefaults = (
  fields: FieldDefinition[],
  data: Record<string, any> = {}
): Record<string, boolean> => {
  const defaults: Record<string, boolean> = {}

  for (const field of fields) {
    if (field.type !== 'boolean') continue
    if (field.containerField) continue
    if (ROW_SECTIONS.includes(field.section)) continue

    const current = data[field.name]
    if (current !== undefined && current !== null && current !== '') continue

    defaults[field.name] = field.defaultValue === true || field.defaultValue === 'true'
  }

  return defaults
}

/** Nombres de campo que una fórmula usa como entrada. */
const formulaInputs = (field: FieldDefinition): string[] => {
  const formula = field.calculatedMetadata?.formula
  if (!formula) return []

  const names: unknown[] = [
    ...(formula.fields || []),
    ...(field.calculatedMetadata?.updateTriggers || [])
  ]

  // Un conditionalAdvanced referencia sus campos dentro de la configuración, no
  // siempre en formula.fields
  const config = formula.parameters?.conditionalConfig
  if (config) {
    const operands = (c: any) => [
      c.leftOperandType === 'field' ? c.leftOperand : null,
      c.rightOperandType === 'field' ? c.rightOperand : null
    ]
    names.push(...operands(config))
    for (const branch of config.branches || []) {
      for (const condition of branch.conditions || []) names.push(...operands(condition))
    }
  }

  return names.filter((n): n is string => typeof n === 'string' && n !== '')
}

/**
 * Los booleanos que alguna fórmula usa como entrada y que el documento no tiene.
 *
 * Es el subconjunto de booleanFieldDefaults que de verdad afecta a un cálculo, para
 * poder rellenarlos al abrir un embarque viejo sin escribir campos que a nadie le
 * importan.
 */
export const calculatedBooleanDefaults = (
  fields: FieldDefinition[],
  data: Record<string, any> = {}
): Record<string, boolean> => {
  const inputs = new Set(fields.flatMap(formulaInputs))
  const defaults = booleanFieldDefaults(fields, data)

  return Object.fromEntries(
    Object.entries(defaults).filter(([name]) => inputs.has(name))
  )
}
